import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createBus,
  createEticketingContent,
  createEticketingTiming,
  createRoutePrice,
  createService,
  deleteBus,
  deleteEticketingTiming,
  deleteRoutePrice,
  deleteService,
  fetchAllBuses,
  fetchAllCities,
  fetchAllPrices,
  fetchAllRoutes,
  fetchAllServices,
  fetchAllUsersRoles,
  fetchDashboardFilters,
  fetchDashboardStats,
  fetchEticketingBookings,
  fetchEticketingContents,
  fetchEticketingTickets,
  fetchEticketingTimings,
  getRoutePrices,
  updateBus,
  updateEticketingContent,
  updateEticketingTiming,
  updateRoutePrice,
  updateService,
} from "./e-ticketing.service";
import { createRoute, DeleteRoute, UpdateRoute } from "../../APIS";

export const useEticketingServicesQuery = (params) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchServices = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAllServices(params);
      setData(result?.data?.services || []);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchServices();
  }, []);

  return { data, isLoading, error, refetch: fetchServices };
};

export const useCreateService = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);

  const create = useCallback(async (serviceData) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await createService(serviceData);
      setResponse(data);
      return data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { create, isLoading, error, response };
};

export const useUpdateService = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);

  const update = useCallback(async (serviceId, serviceData) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await updateService(serviceId, serviceData);
      setResponse(data);
      return data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { update, isLoading, error, response };
};

export const useDeleteService = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);

  const remove = useCallback(async (serviceId) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await deleteService(serviceId);
      setResponse(data);
      return data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { remove, isLoading, error, response };
};

export const useEticketingBookingsQuery = (params) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchBookings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchEticketingBookings(params);
      setData(result?.data);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [params?.service_id]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  return { data, isLoading, error, refetch: fetchBookings };
};

export const useEticketingTicketsQuery = (params) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchTickets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchEticketingTickets(params);
      setData(result?.data);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    if (params?.bookingId) fetchTickets();
  }, [params?.bookingId]);

  return { data, isLoading, error, refetch: fetchTickets };
};

export const useEticketingRoutesQuery = (params) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchRoutes = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAllRoutes(params);
      setData(result?.data);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    if (params?.service_id) fetchRoutes();
  }, [params?.service_id]);

  return { data, isLoading, error, refetch: fetchRoutes };
};

export const useEticketingUpdateRouteMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const update = useCallback(async (routeId, routeData) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await UpdateRoute(routeId, routeData);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { update, isLoading, error, response };
}
export const useEticketingCreateRouteMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const create = useCallback(async (routeData) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await createRoute(routeData);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { create, isLoading, error, response };
}

export const useEticketingDeleteRouteMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const deleteRoute = useCallback(async (routeId) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await DeleteRoute(routeId);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { deleteRoute, isLoading, error, response };
}

export const useEticketingContentsQuery = (params) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchContents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchEticketingContents(params);
      setData(result?.data?.contents?.[0]);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    if (params?.service_id) fetchContents();
  }, [params?.service_id]);

  return { data, isLoading, error, refetch: fetchContents };
};

export const useEticketingCreateContentMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const create = useCallback(async (contentData) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await createEticketingContent(contentData);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { create, isLoading, error, response };
}

export const useEticketingUpdateContentMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const update = useCallback(async (contentId, contentData) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await updateEticketingContent(contentId, contentData);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { update, isLoading, error, response };
}

export const useEticketingBusesQuery = (params) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchBuses = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAllBuses(params);
      setData(result?.data);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchBuses();
  }, [params?.route_id]);

  return { data, isLoading, error, refetch: fetchBuses };
};

export const useEticketingCreateBusMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const create = useCallback(async (busData) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await createBus(busData);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { create, isLoading, error, response };
}
export const useEticketingUpdateBusMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const update = useCallback(async (busId, busData) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await updateBus(busId, busData);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { update, isLoading, error, response };
}

export const useEticketingDeleteBusMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const remove = useCallback(async (busId) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await deleteBus(busId);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { remove, isLoading, error, response };
}

export const useEticketingCitiesQuery = (params) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchCities = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAllCities(params);
      setData(result?.data?.cities || []);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchCities();
  }, []);

  return { data, isLoading, error, refetch: fetchCities };
};

export const useEticketingRoutePricesQuery = (params) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const get = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAllPrices(params);
      setData(result?.data);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    get();
  }, [params?.route_id]);

  return { data, isLoading, error, refetch: get };
};

export const useEticketingCreateRoutePriceMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const create = useCallback(async (routePriceId, priceData) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await createRoutePrice(routePriceId, priceData);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { create, isLoading, error, response };
}
export const useEticketingUpdateRoutePriceMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const update = useCallback(async (routePriceId, priceData) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await updateRoutePrice(routePriceId, priceData);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { update, isLoading, error, response };
}

export const useEticketingDeleteRoutePriceMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const remove = useCallback(async (routePriceId) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await deleteRoutePrice(routePriceId);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { remove, isLoading, error, response };
}

export const useEticketingTimingsQuery = (params) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const get = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchEticketingTimings(params);
      setData(result?.data);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    if (params?.route_id) get();
  }, [params?.route_id]);

  return { data, isLoading, error, refetch: get };
};

export const useEticketingCreateTimingsMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const create = useCallback(async (timingData) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await createEticketingTiming(timingData);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { create, isLoading, error, response };
}
export const useEticketingUpdateTimingMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const update = useCallback(async (timingId, timingData) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await updateEticketingTiming(timingId, timingData);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { update, isLoading, error, response };
}

export const useEticketingDeleteTimingMutation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const remove = useCallback(async (timingId) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await deleteEticketingTiming(timingId);
      setResponse(data);
      return data;
    }
    catch (err) {
      setError(err);
      throw err;
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  return { remove, isLoading, error, response };
}

export const useEticketingDashboardStatsQuery = (params) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Memoized key to avoid unstable object reference
  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  const get = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchDashboardStats(params);
      setData(result?.data);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [paramsKey]); // Only changes when params logically change

  useEffect(() => {
    get(); // Fetch when paramsKey changes (params changed)
  }, [get]);

  return { data, isLoading, error, refetch: get };
};

export const useEticketingDashboardFiltersQuery = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const get = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchDashboardFilters();
      setData(result?.data);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    get();
  }, []);

  return { data, isLoading, error, refetch: get };
};

export const useEticketingUsersRolesQuery = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchUsersRoles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAllUsersRoles();
      setData(result?.data || []);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsersRoles();
  }, []);

  return { data, isLoading, error, refetch: fetchUsersRoles };
};